<template>
    <div class="relative h-input w-full">
        <div class="relative flex h-input w-full cursor-pointer select-none">
            <MultiSelect
                ref="multiSelect"
                v-model="setSelectVal"
                :class="{ 'border-error': errorMessage }"
                :classes="{
                    container: `peer ${
                        small ? 'h-7' : 'h-input'
                    } relative bg-white dark:bg-gray-900 flex items-center w-16 min-w-[4rem] justify-end cursor-pointer border border-r-0 border-gray-300 focus:border-focus text-sm text-muval-gray-1 leading-snug outline-none ${
                        rounded ? 'rounded-full pl-2' : 'rounded-input rounded-r-none'
                    } ${errorMessage ? '!border-error' : ''}`,
                    containerDisabled: 'cursor-default bg-muval-gray-5',
                    containerOpen: `${rounded ? 'rounded-full' : 'rounded-input'}`,
                    containerOpenTop: `${rounded ? 'rounded-full' : 'rounded-input'}`,
                    containerActive: 'border border-focus',
                    singleLabel:
                        'flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3.5',
                    multipleLabel:
                        'flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3.5',
                    search: `w-full absolute ${
                        small ? 'h-6' : 'h-[2.2rem]'
                    } focus:ring-0 inset-0 outline-none appearance-none border-1 border-gray-300 text-sm font-sans dark:bg-gray-900 -pb-4 ${
                        rounded ? 'rounded-full' : 'rounded-input'
                    } pl-3.5 outline-0 focus:outline-0 border-0`,
                    tags: 'flex-grow flex-shrink flex flex-wrap items-center mt-1 pl-2',
                    tag: 'bg-brand text-white text-sm font-semibold pl-2 rounded  flex items-center whitespace-nowrap',
                    tagDisabled: 'pr-2 !bg-gray-400 text-white',
                    tagRemove:
                        'flex items-center justify-center p-1 mx-0.5 rounded-sm hover:bg-black hover:bg-opacity-10 group',
                    tagRemoveIcon:
                        'bg-multiselect-remove bg-center bg-no-repeat opacity-30 inline-block w-3 h-3 group-hover:opacity-60',
                    tagsSearchWrapper: 'inline-block relative mx-1 flex-grow flex-shrink h-full',
                    tagsSearch:
                        'absolute focus:ring-0 inset-0 outline-0 appearance-none p-0 text-sm bg-transparent font-sans box-border w-full border-0 focus:border-0 focus:outline-0',
                    tagsSearchCopy: 'invisible whitespace-pre-wrap inline-block h-px',
                    placeholder: `flex text-gray-400 truncate items-center h-full absolute left-0 placeholder-transparent top-[0.09rem] pointer-events-none bg-transparent leading-snug pl-3.5`,
                    caret: 'text-muval-gray-1 bg-no-repeat w-2.5 h-2 box-content mr-3.5 relative opacity-40 flex-shrink-0 flex-grow-0 transition-transform transform',
                    caretOpen: 'rotate-180',
                    clear: 'text-white pr-3.5 relative z-10 text-white opacity-40 transition duration-300 flex-shrink-0 flex-grow-0 flex hover:opacity-80',
                    clearIcon: 'text-white  bg-center bg-no-repeat w-2.5 box-content inline-block',
                    spinner:
                        'bg-multiselect-spinner bg-center bg-no-repeat w-4 h-4 z-10 mr-3.5 animate-spin flex-shrink-0 flex-grow-0',
                    dropdown:
                        'absolute -left-px -right-px -bottom-1 transform translate-y-full shadow-md -mt-px overflow-y-auto max-h-[250px] z-50 bg-white dark:bg-gray-900 flex flex-col rounded-md',
                    dropdownTop: '-translate-y-full top-px bottom-auto flex-col-reverse rounded-b-none rounded-t',
                    dropdownHidden: 'hidden',
                    groupLabel:
                        'flex text-xs box-border text-muval-gray-2 -ml-1 items-center justify-start text-left py-2 px-3 cursor-default leading-normal',
                    options: 'flex flex-col p-0 m-0 list-none',
                    optionsTop: 'flex-col-reverse',
                    option: 'flex items-center justify-start box-border text-left cursor-pointer text-sm leading-snug py-2 px-3',
                    optionPointed: 'text-gray-800 bg-muval-gray-6',
                    optionSelected: 'text-muval-gray-1 bg-muval-gray-5',
                    optionDisabled: 'text-gray-300 cursor-not-allowed',
                    optionSelectedPointed: 'text-muval-gray-1 bg-muval-gray-5 bg-opacity-70',
                    optionSelectedDisabled: 'text-orange-100 bg-orange-500 bg-opacity-50 cursor-not-allowed',
                    noOptions: 'px-3 text-gray-600 dark:bg-gray-900',
                    noResults: 'px-3 py-2 text-gray-600 dark:bg-gray-900',
                    fakeInput:
                        'bg-transparent absolute left-0 right-0 -bottom-px w-full h-px border-0 p-0 appearance-none outline-none text-transparent border',
                    spacer: 'h-[1rem]',
                }"
                :caret="true"
                :track-by="searchable"
                :options="selectOptions"
            >
                <template #caret>
                    <svg
                        class="mr-0"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        @mousedown.prevent.stop="multiSelect.isOpen = !multiSelect.isOpen"
                    >
                        <path
                            d="M15.9625 9.2925L12.0825 13.1725L8.2025 9.2925C7.8125 8.9025 7.1825 8.9025 6.7925 9.2925C6.4025 9.6825 6.4025 10.3125 6.7925 10.7025L11.3825 15.2925C11.7725 15.6825 12.4025 15.6825 12.7925 15.2925L17.3825 10.7025C17.7725 10.3125 17.7725 9.6825 17.3825 9.2925C16.9925 8.9125 16.3525 8.9025 15.9625 9.2925Z"
                            fill="#393939"
                        />
                    </svg>
                </template>
            </MultiSelect>
            <input
                :id="vid"
                ref="input"
                :disabled="disabled"
                :name="vid"
                :type="inputType"
                class="peer block h-input w-full resize-y overflow-y-auto rounded-input rounded-l-none border border-gray-300 px-3 py-2 text-sm text-muval-gray-1 placeholder-transparent focus:border-l focus:border-focus focus:outline-none focus:ring-0 peer-focus:border-l-focus"
                :class="{
                    '!border-error': errorMessage,
                    'bg-white': !errorMessage,
                    'placeholder-gray-400': !label && placeholder,
                    'bg-muval-gray-5': disabled,
                    'posthog-sensitive': censor,
                }"
                :placeholder="placeholder ? placeholder : label"
                :value="displayValue"
                autocomplete="no"
                @change="(e) => (!updateOnInput ? handleChange(e) : null)"
                @input="(e) => (updateOnInput ? handleChange(e) : null)"
                @blur="handleBlur"
            />
            <div
                v-if="label"
                class="peer pointer-events-none absolute -top-2 left-[4.5rem] z-10 line-clamp-1 text-xs text-gray-400 transition-all duration-75 peer-placeholder-shown:top-[0.6125rem] peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:-top-2 peer-focus:text-xs peer-focus:text-focus"
            >
                <span class="px-1">{{ label }}</span>
                <div class="absolute top-2 -z-10 h-0.5 w-full bg-white px-2 dark:bg-gray-900"></div>
            </div>
            <div
                v-if="cubic"
                :class="errorMessage ? 'border-error' : 'border-[#D0D0D0]'"
                class="rounded-r-muval peer pointer-events-none absolute right-0 top-0 z-0 h-input w-1/5 border border-l-0 border-gray-300 peer-focus:border-focus"
            >
                <div class="absolute right-0 p-2 align-middle text-sm text-gray-900">
                    <span>m&sup3;</span>
                </div>
            </div>

            <div
                v-if="infoType"
                :class="errorMessage ? 'border-error' : 'border-[#D0D0D0]'"
                class="rounded-r-muval peer pointer-events-none absolute right-0 top-0 z-0 flex h-input w-auto border border-l-0 border-gray-300 px-4 text-muval-gray-1 peer-focus:border-focus"
            >
                <div class="relative my-auto mr-auto">
                    <span class="text-sm">{{ infoType }}</span>
                    <div
                        v-if="errorMessage && infoType"
                        class="absolute -left-5 top-1.5"
                    >
                        <svg
                            width="14"
                            height="13"
                            viewBox="0 0 14 13"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            class="text-error"
                        >
                            <path
                                d="M7.24598 9.71591L5.92852 9.71591L5.92852 8.42046L7.24598 8.42046L7.24598 9.71591ZM7.24598 7.125L5.92852 7.125L5.92852 3.23864L7.24598 3.23864L7.24598 7.125ZM6.58725 12.9546C10.2234 12.9546 13.1746 10.0527 13.1746 6.47728C13.1746 2.90182 10.2234 -7.30522e-07 6.58725 -1.0431e-06C2.95106 -1.35567e-06 -5.34657e-05 2.90182 -5.37836e-05 6.47727C-5.41014e-05 10.0527 2.95106 12.9546 6.58725 12.9546ZM6.58725 1.29545C9.49226 1.29545 11.8571 3.6208 11.8571 6.47728C11.8571 9.33375 9.49226 11.6591 6.58725 11.6591C3.68225 11.6591 1.31741 9.33375 1.31741 6.47727C1.31741 3.6208 3.68225 1.29545 6.58725 1.29545Z"
                                fill="currentColor"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            <div
                v-if="errorMessage && !infoType"
                class="absolute right-2 top-3"
            >
                <svg
                    width="14"
                    height="13"
                    viewBox="0 0 14 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="text-error"
                >
                    <path
                        d="M7.24598 9.71591L5.92852 9.71591L5.92852 8.42046L7.24598 8.42046L7.24598 9.71591ZM7.24598 7.125L5.92852 7.125L5.92852 3.23864L7.24598 3.23864L7.24598 7.125ZM6.58725 12.9546C10.2234 12.9546 13.1746 10.0527 13.1746 6.47728C13.1746 2.90182 10.2234 -7.30522e-07 6.58725 -1.0431e-06C2.95106 -1.35567e-06 -5.34657e-05 2.90182 -5.37836e-05 6.47727C-5.41014e-05 10.0527 2.95106 12.9546 6.58725 12.9546ZM6.58725 1.29545C9.49226 1.29545 11.8571 3.6208 11.8571 6.47728C11.8571 9.33375 9.49226 11.6591 6.58725 11.6591C3.68225 11.6591 1.31741 9.33375 1.31741 6.47727C1.31741 3.6208 3.68225 1.29545 6.58725 1.29545Z"
                        fill="currentColor"
                    />
                </svg>
            </div>
        </div>
        <div
            class="pointer-events-none absolute -bottom-5 left-0 w-full truncate whitespace-nowrap text-xxs"
            :class="errorMessage ? 'text-error' : ''"
        >
            <span class="text-xxs">{{ errorMessage }}</span>
        </div>
    </div>
</template>

<script setup>
import { useField } from 'vee-validate';
import { idGen } from '@utilities/idGen';
import MultiSelect from '@vueform/multiselect';

const emit = defineEmits(['update:selected']);

const multiSelect = ref();
const setSelectVal = ref([]);
const rawInput = ref('');

const props = defineProps({
    label: {
        type: [String, null],
        required: false,
        default: null,
    },
    placeholder: {
        type: [String, null],
        required: false,
        default: null,
    },
    disabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    vid: {
        type: String,
        required: false,
        default: () => idGen(6),
    },
    inputType: {
        type: String,
        required: false,
        default: 'text',
    },
    cubic: {
        type: Boolean,
        required: false,
        default: false,
    },
    infoType: {
        type: [String, null],
        required: false,
        default: null,
    },
    modelValue: {
        type: [String, Number, null],
        required: false,
        default: null,
    },
    selectOptions: {
        type: Array,
        required: false,
        default: () => ['+61', '+64', '+63', '+1', '+44', '+91'],
    },
    rounded: {
        type: Boolean,
        required: false,
        default: false,
    },
    small: {
        type: Boolean,
        required: false,
        default: false,
    },
    searchable: {
        type: String,
        required: false,
        default: 'label',
    },
    isSigning: {
        type: Boolean,
        required: false,
        default: false,
    },
    isStoring: {
        type: Boolean,
        required: false,
        default: false,
    },
    combine: {
        type: Boolean,
        required: false,
        default: false,
    },
    censor: {
        type: Boolean,
        required: false,
        default: false,
    },
    /**
     * Emit on every keystroke rather than on `change`, so a caller whose control
     * state depends on what has been typed does not lag a blur behind. Mirrors
     * MuvalInput's prop of the same name, and defaults off for the same reason.
     */
    updateOnInput: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const getSelectedCode = () => {
    const val = setSelectVal.value;
    return (Array.isArray(val) ? val[0] : val) || props.selectOptions[0] || '+61';
};

const parseE164 = (value) => {
    if (!value || typeof value !== 'string') return { code: props.selectOptions[0] || '+61', number: '' };
    const codes = [...props.selectOptions].sort((a, b) => b.length - a.length);
    for (const code of codes) {
        if (value.startsWith(code)) {
            return { code, number: value.slice(code.length) };
        }
    }
    return { code: props.selectOptions[0] || '+61', number: value };
};

// A number carrying its own "+" (international, code not in the picker) is kept
// verbatim — never prefixed with the picker's code on top.
const combineValue = () => {
    if (!rawInput.value) return '';
    if (rawInput.value.startsWith('+')) return rawInput.value;
    return `${getSelectedCode()}${rawInput.value.replace(/^0/, '')}`;
};

const displayValue = computed(() => (props.combine ? rawInput.value : inputValue.value));

const vid = toRef(props, 'vid');
const {
    value: inputValue,
    errorMessage,
    handleBlur,
    handleChange: _handleChange,
} = useField(vid, undefined, {
    syncVModel: true,
});

const handleChange = (e) => {
    if (props.combine) {
        const typed = e.target.value;
        const cleaned = typed.replace(/\s+/g, '');
        if (cleaned.startsWith('+')) {
            const parsed = parseE164(cleaned);
            if (parsed.number !== cleaned) {
                rawInput.value = parsed.number;
                if (parsed.code !== getSelectedCode()) {
                    setSelectVal.value = parsed.code;
                }
            } else {
                rawInput.value = cleaned;
            }
        } else {
            rawInput.value = cleaned;
        }
        _handleChange(combineValue());
    } else {
        _handleChange(e);
    }
};

watch(setSelectVal, () => {
    if (!props.combine) return;
    _handleChange(combineValue());
});

watch(inputValue, (newVal) => {
    if (props.combine) {
        const currentCombined = combineValue();
        if (newVal !== currentCombined) {
            const parsed = parseE164(newVal);
            rawInput.value = parsed.number;
            if (parsed.code !== getSelectedCode()) {
                setSelectVal.value = parsed.code;
            }
        }
    }
    emit('update:selected', { vid: newVal, selected: setSelectVal.value });
});

onMounted(() => {
    if (props.combine && inputValue.value) {
        const parsed = parseE164(inputValue.value);
        rawInput.value = parsed.number;
        multiSelect.value?.select(parsed.code);
    } else {
        multiSelect.value?.select(props.selectOptions[0] || '+61');
    }
});
</script>

<style scoped>
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
    transition: background-color 5000s ease-in-out 0s;
}
</style>
